import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Observable, Subject, zip} from 'rxjs';
import {AuthService} from '../_services/auth.service';
import {UserService} from '../_services/user.service';
import {UtilitiesService} from '../_services/utilities.service';
import {JOB_REQUEST_STATUS, JOB_STATUS, USER_EVENT_TYPE} from '../constants';
import {EventTrackingService} from "../_services/event-tracking.service";
import {mergeMap} from "rxjs/operators";
import {GetVehiclesResponse, Vehicle} from "../../shared/_models/vehicle";
import {JobRequestService} from "../../booking-flow/services/job-request.service";
import {GoogleAnalyticsGuard} from "./ga.guard";
import {LandingPageDetailGuard} from "./landing-page-detail.guard";
import {CampaignEventsService} from "../../booking-flow/services/campaign-events.service";
import {of} from "rxjs/observable/of";
import {AddVehicleResponse} from "../../shared/_models/addVehicle";
import {Angulartics2GoogleTagManager} from "angulartics2/gtm";
import {catchError} from "rxjs/internal/operators";

@Injectable({
  providedIn: 'root'
})
export class CarvanaGuard implements CanActivate {

    userData: any = {};
    selectedVin = "";

    next: ActivatedRouteSnapshot;
    state: RouterStateSnapshot;

    unsubscribe$ = new Subject<void>();

    component = 'carvana-guard';

    constructor(private router: Router,
                private authService: AuthService,
                private userService: UserService,
                private utilitiesService: UtilitiesService,
                private eventTrackingService: EventTrackingService,
                private campaignEventsService: CampaignEventsService,
                private angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
                private jobRequestService: JobRequestService) {
    }

    canActivate(next: ActivatedRouteSnapshot,
                state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

        this.next = next;
        this.state = state;

        let params: any = {};
        window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
            params[key] = decodeURI(value);
            return '';
        });

        if (!UtilitiesService.isEmpty(params.vin)) {
            this.selectedVin = params.vin;
        }
        // if they aren't logged in, just continue
        if (!this.authService.loggedIn()) {
            new GoogleAnalyticsGuard(this.router, this.userService, this.authService, this.angulartics2GoogleTagManager, this.utilitiesService).canActivate(this.next, this.state);
            return new LandingPageDetailGuard(this.router, this.authService, this.utilitiesService, this.campaignEventsService, this.userService).canActivate(this.next, this.state);
        } else if (UtilitiesService.isEmpty(this.selectedVin)) {
            return this.checkJRsAndNavigate();
        } else {
            let vehicleExists: boolean = false;

            this.userService.getUserData().subscribe((userData: any) => {
                    this.userData = userData;
            });

            return this.userService.getVehicles(this.component).pipe(
                mergeMap((result: GetVehiclesResponse) => {
                    if (result.vehicles.length > 0) {
                        const existingVehicle = result.vehicles.find((vehicle: any) => {
                            return vehicle.vin === this.selectedVin;
                        });
                        if (existingVehicle) {
                            vehicleExists = true;
                            this.jobRequestService.setVehicle(existingVehicle);
                        }

                    }
                    if (!vehicleExists) {
                        return this.lookupVehicle();
                    } else {
                        return this.checkJRsAndNavigate();
                    }
                }),
                catchError(error => this.checkJRsAndNavigate())
            );
        }
    } // canActivate()

    checkJRsAndNavigate(): Observable<boolean> {
        // get jobs and job requests concurrently and determine if we need to redirect the user
        return zip(this.userService.findJobRequests(), this.userService.findJobs({statuses: [1, 3, 4, 5, 6]})).pipe(
            mergeMap(responseArray => {
                // job requests will be first entry in array
                // if they have a job request in status new, take them to the appointments page
                // if they have an outstanding job request less than two weeks old, take them to the quote page
                const jobRequestResponse: any = responseArray[0];
                // if vin matches vehicle they have, .filter jobRequests, remove all jobs that don't match vin
                if (!UtilitiesService.isEmpty(this.selectedVin)) {
                    jobRequestResponse.jobRequests = jobRequestResponse.jobRequests.filter((jobRequest: any) => {
                        if (this.selectedVin == jobRequest.vehicleVin) {
                            return jobRequest;
                        }
                    });
                }
                if (!UtilitiesService.isEmpty(jobRequestResponse) && !UtilitiesService.isEmpty(jobRequestResponse.jobRequests)) {
                    const createDate = UtilitiesService.dateFromString(jobRequestResponse.jobRequests[0].createDate);
                    const twoWeeksAgo = new Date().getTime() - 432000000;
                    if (jobRequestResponse.jobRequests[0].status == JOB_REQUEST_STATUS.READY) {
                        this.router.navigate(['/appointments'], {replaceUrl: true});
                        return of(false);
                    } else if (createDate.getTime() > twoWeeksAgo) {
                        this.router.navigate(['/quotes'], {replaceUrl: true});
                        return of(false);
                    }
                }

                // jobs are second entry in array
                const jobResponse: any = responseArray[1];
                // if vin matches vehicle they have, .filter jobRequests, remove all jobs that don't match vin
                if (!UtilitiesService.isEmpty(this.selectedVin)) {
                    jobResponse.jobs = jobResponse.jobs.filter((jobs: any) => {
                        if (this.selectedVin == jobs.vehicleVin) {
                            return jobs;
                        }
                    });
                }
                if (!UtilitiesService.isEmpty(jobResponse) && !UtilitiesService.isEmpty(jobResponse.jobs)) {

                    // most recent job is always first
                    const job  = jobResponse.jobs[0];

                    // if they have an active or upcoming job, send them to the appointments page
                    switch (job.status) {
                        case JOB_STATUS.SCHEDULED:
                        case JOB_STATUS.ENROUTE:
                        case JOB_STATUS.WORKING:
                            this.router.navigate(['/appointments'], {replaceUrl: true});
                            return of(false);
                        default:
                        // do nothing
                    }

                    // if they have a completed past job within the last 5 days, go to the appointments page
                    const startDate = this.utilitiesService.dateFromString(job.startDate);
                    const fiveDaysAgo = new Date().getTime() - 432000000;
                    if (job.status == JOB_STATUS.COMPLETE && startDate.getTime() > fiveDaysAgo) {
                        this.router.navigate(['/appointments'], {replaceUrl: true});
                        return of(false);
                    }
                }

                new GoogleAnalyticsGuard(this.router, this.userService, this.authService, this.angulartics2GoogleTagManager, this.utilitiesService).canActivate(this.next, this.state);
                return new LandingPageDetailGuard(this.router, this.authService, this.utilitiesService, this.campaignEventsService, this.userService).canActivate(this.next, this.state);
            })
        );
    }

    lookupVehicle(): Observable<boolean> {
        const vinData = {
            vin: this.selectedVin
        } as Vehicle;

        return this.userService.lookupVehicle(vinData, this.component).pipe(
            mergeMap((result: any) => {
                if (result.responseCode == 100 && !UtilitiesService.isEmpty(result.vehicle)) {
                    return this.addVehicle(result.vehicle);
                } else {
                    return this.checkJRsAndNavigate();
                }
            })
        ).catch((error: any) => {
            return this.checkJRsAndNavigate();
        });
    } // lookupVehicle()

    addVehicle(vehicle: any): Observable<boolean> {
        vehicle.userId = this.userData.id;

        return this.userService.addVehicle(vehicle, this.component).pipe(
            mergeMap((result: AddVehicleResponse) => {
                if (result.responseCode == 100) {
                    this.jobRequestService.setVehicle(result.vehicle);
                }
                this.eventTrackingService.trackSuccessEvent(this.component, 'success', USER_EVENT_TYPE.ADD_VEHICLE, '/user/addvehicle', vehicle, result.responseCode);
                return this.checkJRsAndNavigate();
            })
        ).catch((error: any) => {
            this.eventTrackingService.trackErrorEvent(this.component, 'error', USER_EVENT_TYPE.ADD_VEHICLE, '/user/addvehicle', vehicle, error.responseCode);
            return this.checkJRsAndNavigate();
        });
    } // lookupVehicle()

    unsubscribe() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    } // unsubscribe()
}
